import {
    Phone,
    MessageCircle,
    Clock,
    Wallet,
    Headset,
    ShieldCheck,
    ArrowUpRight,
} from "lucide-react";
import { PageHeader, FadeUp } from "../components/Reveal";
import { BRAND } from "../data/siteData";

export default function Support() {
    return (
        <>
            <PageHeader
                testId="support-header"
                eyebrow="CUSTOMER SUPPORT"
                title={
                    <>
                        Need Help?{" "}
                        <span className="text-gradient-fire">We're Here For You.</span>
                    </>
                }
                subtitle="Accessible support from a trusted local provider — real people, right here in Narasaraopet."
            />

            <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
                <div className="grid gap-6 lg:grid-cols-2">
                    <FadeUp className="h-full">
                        <div
                            data-testid="support-care-card"
                            className="glass flex h-full flex-col rounded-3xl p-8"
                        >
                            <div className="flex items-center gap-4">
                                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#E61C24]/15 text-[#ff5a60]">
                                    <Headset className="h-7 w-7" />
                                </span>
                                <div>
                                    <h2 className="font-display text-2xl font-bold text-white">
                                        Customer Care
                                    </h2>
                                    <p className="flex items-center gap-2 text-sm text-slate-400">
                                        <Clock className="h-4 w-4 text-[#00F0FF]" />
                                        {BRAND.careHours}
                                    </p>
                                </div>
                            </div>

                            <div className="mt-8 grid gap-3 sm:grid-cols-2">
                                {BRAND.phones.map((p, i) => (
                                    <a
                                        key={p.raw}
                                        href={p.tel}
                                        data-testid={`support-call-button-${i + 1}`}
                                        className="group flex items-center justify-center gap-2.5 rounded-2xl bg-[#E61C24] px-5 py-4 font-mono text-base font-bold tracking-wider text-white shadow-[0_10px_30px_rgba(230,28,36,0.4)] transition-[transform,background-color] duration-200 hover:scale-[1.03] hover:bg-[#C7151C]"
                                    >
                                        <Phone className="h-4 w-4" />
                                        {p.label}
                                    </a>
                                ))}
                            </div>

                            <div className="mt-4 flex flex-wrap gap-3">
                                <a
                                    href={BRAND.phones[0].tel}
                                    data-testid="support-call-care-button"
                                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-white/20 px-6 py-3.5 text-sm font-bold text-white transition-[border-color,color,transform] duration-200 hover:scale-[1.02] hover:border-[#00F0FF]/70 hover:text-[#00F0FF]"
                                >
                                    <Phone className="h-4 w-4" />
                                    Call Customer Care
                                </a>
                                <a
                                    href={BRAND.whatsapp}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="support-whatsapp-button"
                                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-[#1FAF54] px-6 py-3.5 text-sm font-bold text-white transition-[transform,background-color] duration-200 hover:scale-[1.02] hover:bg-[#178f43]"
                                >
                                    <MessageCircle className="h-4 w-4" />
                                    WhatsApp Us
                                </a>
                            </div>
                        </div>
                    </FadeUp>

                    <FadeUp delay={0.12} className="h-full">
                        <div
                            data-testid="pay-online-card"
                            className="relative flex h-full flex-col overflow-hidden rounded-3xl border border-[#00F0FF]/25 bg-gradient-to-b from-[#00F0FF]/8 to-[#131927] p-8"
                        >
                            <div
                                className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#00F0FF]/15 blur-3xl"
                                aria-hidden="true"
                            />
                            <div className="relative flex items-center gap-4">
                                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#00F0FF]/12 text-[#00F0FF]">
                                    <Wallet className="h-7 w-7" />
                                </span>
                                <div>
                                    <h2 className="font-display text-2xl font-bold text-white">
                                        Pay Your Bill Online
                                    </h2>
                                    <p className="text-sm text-slate-400">
                                        Instant Eway Fiber Net bill payment
                                    </p>
                                </div>
                            </div>

                            <div className="relative mt-8 space-y-4 rounded-2xl border border-white/10 bg-black/30 p-6">
                                <p className="font-mono text-[11px] font-bold tracking-[0.26em] text-slate-400">
                                    PAYTM · HOME BILLS · WIFI / LANDLINE
                                </p>
                                <p
                                    className="font-display text-3xl font-black tracking-tight text-white sm:text-4xl"
                                    data-testid="pay-online-provider"
                                >
                                    Eway <span className="text-gradient-fire">Fiber Net</span>
                                </p>
                                <p className="flex items-center gap-2 text-sm font-semibold text-white">
                                    <ShieldCheck className="h-4 w-4 text-emerald-400" />
                                    Official Paytm bill-payment portal
                                </p>
                            </div>

                            <a
                                href={BRAND.links.payNow}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="support-pay-now-button"
                                className="group relative mt-6 inline-flex w-fit items-center gap-2.5 rounded-full bg-[#00F0FF] px-8 py-4 text-base font-extrabold tracking-wide text-[#0B0F19] shadow-[0_10px_40px_rgba(0,240,255,0.3)] transition-[transform,background-color] duration-200 hover:scale-105 hover:bg-[#5df6ff]"
                            >
                                <Wallet className="h-5 w-5" />
                                PAY NOW
                                <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </a>

                            <p className="relative mt-6 text-sm leading-relaxed text-slate-400">
                                For connection activation, renewal or payment
                                assistance, contact customer care on{" "}
                                <a
                                    href={BRAND.phones[0].tel}
                                    className="font-semibold text-[#ff6b70] hover:text-white"
                                >
                                    {BRAND.phones[0].label}
                                </a>{" "}
                                ({BRAND.careHoursShort}).
                            </p>
                        </div>
                    </FadeUp>
                </div>
            </section>
        </>
    );
}
